import React, { useState } from 'react';
import AboutNav from '../Components/Navigation/AboutNav';
import Footer from '../Components/Home/Footer';
import SEBISlider from '../Components/Common/SEBISlider';
import ISOCertificateButton from '../Components/Common/ISOCertificateButton';
import ScreenshotPrevention from '../Components/Common/ScreenshotPrevention';
import PricingForm from '../Components/PricingForm/PricingForm';
import { FaChartLine, FaUserTie, FaRocket, FaCrown, FaGift, FaCheck, FaBriefcase, FaRobot, FaGraduationCap, FaShieldAlt, FaClock, FaChartBar, FaUserShield } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../Assets/Styles/Pages/services_page.css';
import { Helmet } from 'react-helmet';

const Services_page = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  const handleGetStarted = (planTitle) => {
    setSelectedPlan(planTitle);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedPlan('');
  };

  const pricingData = [
    {
      logo: <FaGift className="pricing-icon" />,
      title: "Free Plan",
      description: "Start your investment journey with our free starter package",
      price: "Free",
      features: [
        "Basic Market Overview",
        "Weekly Market Updates",
        "Community Forum Access",
        "Basic Stock Information",
        "Educational Resources"
      ]
    },
    {
      logo: <FaChartLine className="pricing-icon" />,
      title: "Basic Plan",
      description: "Perfect for beginners starting their investment journey",
      price: "₹15000",
      features: [
        "Basic Stock Research",
        "Daily Market Updates",
        "Email Support",
        "Basic Portfolio Tracking",
        "Weekly Newsletter"
      ]
    },
    {
      logo: <FaUserTie className="pricing-icon" />,
      title: "Premium Plan",
      description: "Ideal for active traders and serious investors",
      price: "₹51000",
      features: [
        "Advanced Stock Research",
        "Real-time Market Alerts",
        "Priority Email & Phone Support",
        "Advanced Portfolio Analytics",
        "Daily Research Reports",
        "Trading Strategies"
      ]
    },
    {
      logo: <FaRocket className="pricing-icon" />,
      title: "HNI Plan",
      description: "For professional traders and institutional clients",
      price: "₹121000",
      features: [
        "Premium Stock Research",
        "Instant Market Alerts",
        "24/7 Priority Support",
        "Custom Portfolio Solutions",
        "Exclusive Research Reports",
        "Advanced Trading Strategies",
        "Personal Investment Advisor"
      ]
    },
    {
      logo: <FaCrown className="pricing-icon" />,
      title: "Customized Plan",
      description: "For clients who need a tailored investment solution",
      price: "Custom",
      features: [
        "Custom Research Solutions",
        "Dedicated Support Team",
        "Custom Integration",
        "White-label Solutions",
        "Institutional Reports",
        "Dedicated Account Manager"
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services | Trillion Stock Research</title>
        <meta name="description" content="Explore our comprehensive investment advisory, stock research, portfolio management, and trading strategy services at Trillion Stock Research." />
        <meta name="keywords" content="investment advisory, stock research, portfolio management, trading strategies, Trillion Stock Research services" />
        <link rel="canonical" href="https://www.trillionstockresearch.com/services" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Investment Advisory & Stock Research",
            "provider": {
              "@type": "Organization",
              "name": "Trillion Stock Research",
              "url": "https://www.trillionstockresearch.com/"
            },
            "areaServed": "IN"
          }
        `}</script>
      </Helmet>
      <ScreenshotPrevention />
      <div className="services-page">
        <AboutNav />
        <SEBISlider />
      
        {/* Hero Section */}
        <section className="services-hero">
          <div className="hero-content">
            <h1>Our Services</h1>
            <p>Comprehensive Investment Solutions for Your Financial Growth</p>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="services-pricing">
          <div className="services-container">
            <div className="pricing-header">
              <h2>Service Pricing Plans</h2>
              <p>Choose the perfect plan that matches your investment goals and trading style</p>
              <Link to="/pricing" className="pricing-link">View Full Pricing Details →</Link>
            </div>
            
            <div className="pricing-grid">
              {pricingData.map((plan, index) => (
                <div key={index} className="pricing-card">
                  <div className="pricing-card-header">
                    {plan.logo}
                    <h3>{plan.title}</h3>
                    <p className="pricing-description">{plan.description}</p>
                    <div className="pricing-price">{plan.price}</div>
                  </div>
                  <button 
                    className="pricing-cta" 
                    onClick={() => handleGetStarted(plan.title)}
                  >
                    Get Started
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Services Section */}
        <section className="services-main">
          <div className="services-container">
            <div className="services-intro">
              <h2>Investment Advisory Services</h2>
              <p>We provide professional guidance and comprehensive solutions to help you navigate the complex world of investments and achieve your financial goals.</p>
            </div>

            <div className="services-grid">
              {/* Stock Research */}
              <div className="service-card">
                <div className="service-icon">
                  <FaChartLine />
                </div>
                <h3>Stock Research</h3>
                <p>In-depth analysis of stocks, market trends, and investment opportunities with detailed reports and recommendations.</p>
                <ul className="service-features">
                  <li><FaCheck /> Technical Analysis</li>
                  <li><FaCheck /> Fundamental Analysis</li>
                  <li><FaCheck /> Market Research Reports</li>
                </ul>
              </div>

              {/* Portfolio Management */}
              <div className="service-card">
                <div className="service-icon">
                  <FaBriefcase />
                </div>
                <h3>Portfolio Management</h3>
                <p>Professional portfolio management services tailored to your investment objectives and risk tolerance.</p>
                <ul className="service-features">
                  <li><FaCheck /> Asset Allocation</li>
                  <li><FaCheck /> Risk Management</li>
                  <li><FaCheck /> Regular Portfolio Review</li>
                </ul>
              </div>

              {/* Trading Strategies */}
              <div className="service-card">
                <div className="service-icon">
                  <FaRobot />
                </div>
                <h3>Trading Strategies</h3>
                <p>Advanced trading strategies and tools to help you make informed decisions in the market.</p>
                <ul className="service-features">
                  <li><FaCheck /> Algorithmic Trading</li>
                  <li><FaCheck /> Technical Indicators</li>
                  <li><FaCheck /> Risk Assessment</li>
                </ul>
              </div>

              {/* Investment Education */}
              <div className="service-card">
                <div className="service-icon">
                  <FaGraduationCap />
                </div>
                <h3>Investment Education</h3>
                <p>Comprehensive educational resources and training programs to enhance your investment knowledge.</p>
                <ul className="service-features">
                  <li><FaCheck /> Market Workshops</li>
                  <li><FaCheck /> Trading Courses</li>
                  <li><FaCheck /> Professional Webinars</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="services-features">
          <div className="services-container">
            <h2>Why Choose Our Services?</h2>
            <div className="features-grid">
              <div className="feature-item">
                <FaShieldAlt />
                <h3>SEBI Registered</h3>
                <p>Authorized and regulated by SEBI for investment advisory services</p>
              </div>
              <div className="feature-item">
                <FaClock />
                <h3>24/7 Support</h3>
                <p>Round-the-clock assistance for all your investment queries</p>
              </div>
              <div className="feature-item">
                <FaChartBar />
                <h3>Proven Track Record</h3>
                <p>Consistent performance and satisfied clients over the years</p>
              </div>
              <div className="feature-item">
                <FaUserShield />
                <h3>Secure Platform</h3>
                <p>Advanced security measures to protect your investments</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="services-cta">
          <div className="services-container">
            <div className="cta-content">
              <h2>Ready to Start Your Investment Journey?</h2>
              <p>Join thousands of successful investors who trust our professional services</p>
              <button className="cta-button">Get Started Today</button>
            </div>
          </div>
        </section>

        <PricingForm 
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          selectedPlan={selectedPlan}
          pricingData={pricingData}
        />

        <Footer />
        <ISOCertificateButton />
      </div>
    </>
  );
};

export default Services_page;
